import type {
  ApplicationStatus,
  CreateApplicationInput,
  FeePayer,
  PayoutFee,
  PayoutStatus,
  ReceiptView,
  SwiftChargeOption,
} from '@/api/payouts';

/** 普通新建表单的可编辑默认附言；服务端也有同值兜底。 */
export const DEFAULT_APPLICATION_REFERENCE = 'KOL Marketing';

/** 申请状态的展示定义。唯一真相源，别在组件里各写一份 switch。 */
export const APPLICATION_STATUS_LABEL: Record<ApplicationStatus, string> = {
  PENDING: '待打款',
  PROCESSING: '打款中',
  PAID: '已打款',
  FAILED: '打款失败',
  REJECTED: '已拒绝',
};

export const APPLICATION_STATUS_COLOR: Record<ApplicationStatus, string> = {
  PENDING: 'gold',
  PROCESSING: 'blue',
  PAID: 'green',
  FAILED: 'red',
  REJECTED: 'error',
};

export const PAYOUT_STATUS_LABEL: Record<PayoutStatus, string> = {
  // 「已发起」而不是「处理中」：这个状态的真实含义是「可能已经在打款了，结果未知」，
  // 用词必须让人意识到不能重试
  INITIATING: '已发起（结果待确认）',
  PROCESSING: '处理中',
  PAID: '已到账',
  FAILED: '失败',
  CANCELLED: '已取消',
  UNKNOWN: '状态未知（需人工核对）',
};

export const PAYOUT_STATUS_COLOR: Record<PayoutStatus, string> = {
  INITIATING: 'orange',
  PROCESSING: 'blue',
  PAID: 'green',
  FAILED: 'red',
  CANCELLED: 'default',
  UNKNOWN: 'volcano',
};

/**
 * 终态：结果已出，不再计入「未完成」。
 *
 * ⚠️ PAID 的「终态」只是对财务而言的。Airwallex 可能在之后把它改成 CANCELLED（资金退回），
 * 服务端在 PAID 之后还有一段观察期会继续核对（PRODUCT.md §5.5）。界面上这类记录
 * 显示为「已到账后退回」，见 payoutStatusView。
 */
export function isTerminalPayout(s: PayoutStatus): boolean {
  return s === 'PAID' || s === 'FAILED' || s === 'CANCELLED';
}

/**
 * 状态列该显示什么。
 *
 * 🔴 `CANCELLED` 且 `paidAt` 非空 = **到账后被退回**：钱曾经到过对方账户（或至少被
 * 银行确认已付），之后被收款行退票 / 收款人要求退回。财务很可能已经按到账处理过
 * 后续（通知达人、记账），现在要回头处理退款 —— 这和「没打成就被取消」是两件事，
 * 不能都写成「已取消」。agency-crm 生产 4 个月里发生过 5 次，最长在 PAID 后 58 天。
 */
export function payoutStatusView(row: { status: PayoutStatus; paidAt: string | null }): {
  label: string;
  color: string;
} {
  if (row.status === 'CANCELLED' && row.paidAt) {
    return { label: '已到账后退回', color: 'red' };
  }
  return { label: PAYOUT_STATUS_LABEL[row.status], color: PAYOUT_STATUS_COLOR[row.status] };
}

/**
 * 没有确认函可下载时，那一格写什么。
 *
 * 🔴 「还没到」和「永远不会有」必须是两句不同的话。都写「生成中」的话，
 * 一笔被上游当场拒收、根本没产生转账的打款会永远挂着「生成中」，
 * 而没有人知道该等到什么时候 —— 在这个系统里，「不确定」不该长得像「马上就好」。
 *
 * 确认函一有转账就去取（不等打款出结果），所以这四句话说的都是**转账**那条线，
 * 与「钱到没到」无关：
 * - `WAITING` 还没产生转账，无从取起；
 * - `GENERATING` 有转账了，正在取；
 * - `REJECTED` 永远不会有；
 * - `UNAVAILABLE` 有转账但取不到，已放弃。
 */
export const RECEIPT_PLACEHOLDER: Record<Exclude<ReceiptView, 'ARCHIVED'>, string> = {
  WAITING: '未产生转账',
  GENERATING: '生成中',
  REJECTED: '无（上游拒收）',
  UNAVAILABLE: '取不到',
};

/** 鼠标悬停时的解释。空串表示不需要解释。 */
export const RECEIPT_PLACEHOLDER_HINT: Record<Exclude<ReceiptView, 'ARCHIVED'>, string> = {
  WAITING:
    '这笔打款还没在 Airwallex 产生转账（发起中，或发起时没收到回音、正在核对），确认函无从取起。',
  GENERATING: '已在向 Airwallex 取确认函，取不到会自动重试。转账在途时也会取，取到不代表已到账。',
  REJECTED: '这笔打款被 Airwallex 当场拒收，没有产生转账，因此不存在确认函。钱没有动，失败原因见「状态」列。',
  UNAVAILABLE: '反复向 Airwallex 取确认函都没成功，已停止重试。需要的话请到 Airwallex 后台下载。',
};

/** 是不是 SWIFT 收款人 —— 只有它才有「中转行费用」这件事。 */
export function isSwiftPayee(transferMethod: string | null): boolean {
  return transferMethod === 'SWIFT';
}

export const FEE_PAYER_LABEL: Record<FeePayer, string> = {
  PAYER: '我们承担',
  BENEFICIARY: '对方承担',
};

export const SWIFT_CHARGE_LABEL: Record<SwiftChargeOption, string> = {
  PAYER: '我们承担',
  SHARED: '各付各的',
};

/**
 * 金额列第三行的文案。返回 null 表示这一行不出现。
 *
 * 🔴 `amount === null` 是「上游还没给出费用」，必须不显示，而不是显示 `0`。
 * 把「不知道」渲染成一个确定的数字，是这个仓里反复强调的那类错误。
 *
 * 🔴 SWIFT 选了「各付各的」时必须标出来：中转行那部分扣费发生在 Airwallex 之外，
 * 我们拿不到也控制不了，不标的话「对方到手」/「手续费」会被读成一个确定值。
 *
 * 注意 tail 写的是「后果」而不是「谁承担」：`paidBy` 和 `swiftChargeOption`
 * 是界面上两个独立的 Radio.Group（见 PayoutApplications.tsx），
 * BENEFICIARY + SHARED 这个组合是可达的 —— 如果 tail 也写成「对方承担」，
 * 会和前面的 FEE_PAYER_LABEL 重复一遍。中转行扣费在 Airwallex 之外发生，
 * 我们既拿不到具体数字也控制不了，所以只能提醒「这个数还会变」，而不是
 * 暗示这笔扣费也算在某一方头上。
 *
 * 🔴 非终态一律不显示，哪怕 `fee.amount` 已经有值。PRODUCT.md §5.8.2 写的是
 * 「终态之后」才显示上游回传的实际费用——create 响应可能已经带回 fee_amount，
 * 但那笔转账本身还没出结果（INITIATING/PROCESSING/UNKNOWN），把一个可能还会变的
 * 数字摆成确定值挂在一笔「结果待确认」的记录旁边，等于让「不确定」长得像「确定」，
 * 与 §5.6 转账确认函「未到终态时标注不代表已到账」是同一个立场。
 */
export function feeLine(fee: PayoutFee, status: PayoutStatus): string | null {
  if (!isTerminalPayout(status)) return null;
  if (fee.amount === null) return null;
  const head =
    fee.paidBy === 'BENEFICIARY'
      ? `对方到手 ${fee.beneficiaryReceives ?? '—'}`
      : `手续费 ${fee.amount}`;
  const tail = fee.swiftChargeOption === 'SHARED' ? ' · 中转行还会再扣' : '';
  return `${head} · ${FEE_PAYER_LABEL[fee.paidBy]}${tail}`;
}

/**
 * 组装 `/payout-applications/create` 的请求体。
 *
 * 🔴 抽成纯函数是为了能断言「非 SWIFT 时 `swiftChargeOption` 这个键压根不存在」——
 * 这条只有拿到发出去的对象本身才能验，光看返回值分不出「键缺失」和「键为 undefined」。
 * 后端对非 SWIFT 收款人传这个字段会 400（SWIFT_OPTION_NOT_APPLICABLE）。
 */
export function buildApplicationPayload(
  values: { payeeId: string; amount: string; reference: string; note?: string },
  swift: boolean,
  choice: { feePaidBy: FeePayer; swiftChargeOption?: SwiftChargeOption },
): CreateApplicationInput {
  return {
    ...values,
    feePaidBy: choice.feePaidBy,
    ...(swift && choice.swiftChargeOption
      ? { swiftChargeOption: choice.swiftChargeOption }
      : {}),
  };
}
